import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Monitor, Phone, ChefHat, Flame, Salad, Pizza, Coffee, Car,
  ArrowLeft
} from 'lucide-react';

// Station icons mapping
const STATION_ICONS = {
  hauptkasse: Monitor,
  telefon: Phone,
  kueche: ChefHat,
  fritteuse: Flame,
  salat: Salad,
  grill: Flame,
  pizza: Pizza,
  getraenke: Coffee,
  fahrer: Car
};

// Station configurations
const STATIONS = [
  {
    type: 'hauptkasse',
    name: 'Hauptkasse',
    description: 'Vollzugriff auf alle Funktionen',
    color: 'from-amber-500 to-orange-600',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/30'
  },
  {
    type: 'telefon',
    name: 'Telefon',
    description: 'Schnelle Telefonbestellungen',
    color: 'from-blue-500 to-cyan-600',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/30'
  },
  {
    type: 'kueche',
    name: 'Küche',
    description: 'Burger, Hauptgerichte',
    color: 'from-red-500 to-rose-600',
    bgColor: 'bg-red-500/10',
    borderColor: 'border-red-500/30'
  },
  {
    type: 'fritteuse',
    name: 'Fritteuse',
    description: 'Pommes, Fingerfood, Wings',
    color: 'from-orange-500 to-amber-600',
    bgColor: 'bg-orange-500/10',
    borderColor: 'border-orange-500/30'
  },
  {
    type: 'salat',
    name: 'Salatstation',
    description: 'Salate, Wraps, Suppen',
    color: 'from-green-500 to-emerald-600',
    bgColor: 'bg-green-500/10',
    borderColor: 'border-green-500/30'
  },
  {
    type: 'pizza',
    name: 'Pizza-Station',
    description: 'Pizza, Pizzabrötchen',
    color: 'from-rose-500 to-pink-600',
    bgColor: 'bg-rose-500/10',
    borderColor: 'border-rose-500/30'
  },
  {
    type: 'getraenke',
    name: 'Getränke',
    description: 'Getränke, Desserts',
    color: 'from-purple-500 to-violet-600',
    bgColor: 'bg-purple-500/10',
    borderColor: 'border-purple-500/30'
  },
  {
    type: 'fahrer',
    name: 'Fahrer',
    description: 'Lieferungen & Navigation',
    color: 'from-slate-500 to-gray-600',
    bgColor: 'bg-slate-500/10',
    borderColor: 'border-slate-500/30'
  }
];

export default function StationSelector({ businessName, onSelectStation, onLogout }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-8">
      {/* Header */}
      <div className="max-w-6xl mx-auto mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            <img 
              src="/cash-x-logo.png" 
              alt="Cash-X" 
              className="h-16 w-auto object-contain"
            />
            <div>
              <h1 className="text-3xl font-bold text-white mb-1">Station auswählen</h1>
              <p className="text-gray-400">{businessName}</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            onClick={onLogout}
            className="border-gray-600 text-gray-300 hover:bg-gray-700"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Abmelden
          </Button>
        </div>
      </div>

      {/* Station Grid */}
      <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {STATIONS.map((station) => {
          const IconComponent = STATION_ICONS[station.type] || Monitor;
          
          return (
            <Card
              key={station.type}
              data-testid={`station-${station.type}`}
              className={`
                cursor-pointer transition-all duration-300 
                bg-gray-800/50 border-2 ${station.borderColor}
                hover:scale-105 hover:shadow-2xl hover:shadow-${station.color.split('-')[1]}-500/20
                group
              `}
              onClick={() => onSelectStation(station.type)}
            >
              <CardContent className="p-6 text-center">
                {/* Icon */}
                <div className={`
                  w-20 h-20 mx-auto mb-4 rounded-2xl 
                  bg-gradient-to-br ${station.color}
                  flex items-center justify-center
                  group-hover:scale-110 transition-transform duration-300
                  shadow-lg
                `}>
                  <IconComponent className="w-10 h-10 text-white" />
                </div>
                
                {/* Name */}
                <h3 className="text-xl font-bold text-white mb-2">
                  {station.name}
                </h3>
                
                {/* Description */}
                <p className="text-sm text-gray-400">
                  {station.description}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Footer Info */}
      <div className="max-w-6xl mx-auto mt-8 text-center text-gray-500 text-sm">
        <p>Wählen Sie die Station, an der Sie arbeiten möchten.</p>
        <p className="mt-1">Die Ansicht wird entsprechend angepasst.</p>
      </div>
    </div>
  );
}
